import {
	ColumnDef,
	ExpandedState,
	flexRender,
	getCoreRowModel,
	getExpandedRowModel,
	getSortedRowModel,
	SortingState,
	useReactTable,
	VisibilityState,
} from '@tanstack/react-table'
import React, { ReactElement, useState } from 'react'

import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'

interface DataTableProps<TData, TValue> {
	columns: ColumnDef<TData, TValue>[]
	data: TData[]
	expandableComponent?: (item: TData) => ReactElement<any, any>
	canToggleVisibility?: boolean
	subRow?: keyof TData
	isExpandable?: boolean
	isLoading?: boolean
}

export function DataTable<TData, TValue>({
	columns,
	data,
	subRow,
	canToggleVisibility,
	isExpandable,
	expandableComponent,
	isLoading,
}: DataTableProps<TData, TValue>) {
	const [sorting, setSorting] = useState<SortingState>([])
	const [expanded, setExpanded] = useState<ExpandedState>({})
	const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({})

	const table = useReactTable({
		data,
		columns,
		getCoreRowModel: getCoreRowModel(),
		onSortingChange: setSorting,
		getSortedRowModel: getSortedRowModel(),
		onExpandedChange: setExpanded,
		getExpandedRowModel: isExpandable ? getExpandedRowModel() : undefined,
		onColumnVisibilityChange: setColumnVisibility,
		state: {
			sorting,
			expanded,
			columnVisibility,
		},
	})

	return (
		<div className="rounded-md">
			{canToggleVisibility && (
				<div className="flex justify-end max-md:hidden">
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="outline" className="ml-auto border-input font-normal rounded-md p-2">
								Reiter anzeigen
								<ChevronDownIcon className="size-4 opacity-50" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent
							align="end"
							style={{ width: 'var(--radix-dropdown-menu-trigger-width)' }}>
							{table
								.getAllColumns()
								.filter(column => column.getCanHide())
								.map(column => {
									return (
										<DropdownMenuCheckboxItem
											key={column.id}
											className="capitalize"
											checked={column.getIsVisible()}
											onCheckedChange={value => column.toggleVisibility(!!value)}>
											{(column.columnDef.meta as any)?.title ?? column.id}
										</DropdownMenuCheckboxItem>
									)
								})}
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			)}
			<Table className="border-separate border-spacing-y-3">
				<TableHeader>
					{table.getHeaderGroups().map(headerGroup => (
						<TableRow className="bg-[#F7F8F880] rounded-xl h-14" key={headerGroup.id}>
							{headerGroup.headers.map((header, index) => {
								return (
									<TableHead className="px-4 text-center" align="center" key={header.id + index}>
										{header.isPlaceholder
											? null
											: flexRender(header.column.columnDef.header, header.getContext())}
									</TableHead>
								)
							})}
						</TableRow>
					))}
				</TableHeader>
				<TableBody>
					{!isLoading && table.getRowModel().rows?.length ? (
						table.getRowModel().rows.map((row, index) => (
							<React.Fragment key={row.id}>
								<TableRow
									className={cn('border-0 h-14', index % 2 !== 0 ? 'bg-[#F7F8F8]' : 'bg-white', {
										'bg-white!': row.getIsExpanded(),
									})}
									data-state={row.getIsSelected() && 'selected'}>
									{row.getVisibleCells().map((cell, index) => (
										<TableCell
											align="center"
											className={cn('text-secondary text-sm font-medium', {
												'rounded-l-xl': index === 0 && !row.getIsExpanded(),
												'rounded-r-xl':
													index === row.getVisibleCells().length - 1 && !row.getIsExpanded(),
												'border-t border-[#EEEFF0]': row.getIsExpanded(),
												'border-l rounded-tl-xl border-[#EEEFF0]':
													row.getIsExpanded() && index === 0,
												'border-r rounded-tr-xl border-[#EEEFF0]':
													row.getIsExpanded() && index === row.getVisibleCells().length - 1,
											})}
											key={cell.id + index}>
											{flexRender(cell.column.columnDef.cell, cell.getContext())}
										</TableCell>
									))}
								</TableRow>
								{row.getIsExpanded() &&
									subRow &&
									Array.isArray(row.original[subRow]) &&
									row.original[subRow].length > 0 && (
										<TableRow className="hover:bg-white relative -translate-y-5">
											<TableCell
												colSpan={columns.length}
												className="p-0 border border-[#EEEFF0] rounded-b-xl border-t-0">
												{expandableComponent?.(row.original)}
											</TableCell>
										</TableRow>
									)}
							</React.Fragment>
						))
					) : (
						<TableRow>
							<TableCell colSpan={columns.length} className="h-24 text-center">
								{isLoading ? 'Loading Data...' : 'No results'}
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</div>
	)
}

import { Column } from '@tanstack/react-table'
import { ArrowDown, ArrowUp, ChevronDownIcon, ChevronsUpDown } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip'

interface DataTableColumnHeaderProps<TData, TValue> extends React.HTMLAttributes<HTMLDivElement> {
	column: Column<TData, TValue>
	title: string
	showTooltip?: string
	customSortType?: 'asc' | 'desc' | null
	setCustomSortType?: (value: 'asc' | 'desc' | null) => void
	showInfoIcon?: boolean
}

export function DataTableColumnHeader<TData, TValue>({
	column,
	title,
	showInfoIcon,
	customSortType,
	setCustomSortType,
	showTooltip,
	className,
}: DataTableColumnHeaderProps<TData, TValue>) {
	if (!column.getCanSort()) {
		return (
			<div className={cn(className)}>
				{showInfoIcon &&
					(!!showTooltip ? (
						<Tooltip>
							<TooltipTrigger>
								<InformationCircleIcon className="size-4 shrink-0 text-secondary/50" />
							</TooltipTrigger>
							<TooltipContent side="top">
								<p className="text-xs font-medium">{showTooltip}</p>
							</TooltipContent>
						</Tooltip>
					) : (
						<InformationCircleIcon className="size-4 shrink-0 text-secondary/50" />
					))}{' '}
				{title}
			</div>
		)
	}

	return (
		<div className={cn('flex items-center space-x-2', className)}>
			<Button
				variant="ghost"
				size="sm"
				onClick={() => {
					if (customSortType !== undefined && setCustomSortType) {
						if (customSortType === null) {
							setCustomSortType('asc')
						} else if (customSortType === 'asc') {
							setCustomSortType('desc')
						} else {
							setCustomSortType(null)
						}
					} else {
						const isSorted = column.getIsSorted()
						if (isSorted === 'asc') {
							column.toggleSorting(true)
						} else if (isSorted === 'desc') {
							column.clearSorting()
						} else {
							column.toggleSorting(false)
						}
					}
				}}
				className="-ml-3 grow text-center text-muted-foreground text-xs font-medium h-8 data-[state=open]:bg-accent">
				{showInfoIcon &&
					(!!showTooltip ? (
						<Tooltip>
							<TooltipTrigger>
								<InformationCircleIcon className="size-4 shrink-0 text-secondary/50" />
							</TooltipTrigger>
							<TooltipContent
								className="max-w-[186px] flex"
								align="end"
								alignOffset={-20}
								side="top">
								<p className="text-xs text-secondary/50 font-medium">{showTooltip}</p>
							</TooltipContent>
						</Tooltip>
					) : (
						<InformationCircleIcon className="size-4 shrink-0 text-secondary/50" />
					))}
				<span>{title}</span>
				{customSortType === 'desc' || column.getIsSorted() === 'desc' ? (
					<ArrowDown />
				) : customSortType === 'asc' || column.getIsSorted() === 'asc' ? (
					<ArrowUp />
				) : (
					<ChevronsUpDown />
				)}
			</Button>
		</div>
	)
}
